import {
  apiPostMaintenanceReport,
  apiPostMaintenanceReportDraft,
  apiPutMaintenanceReport,
} from "@app/api/machines/machines.api";
import {
  uploadMediaFiles,
  uploadRecording,
  uploadSignature,
} from "../Inspection/createInspectionSubmit.helper";
import { isWorkAtInValidRange } from "../Refuel/createRefuelSubmit.helper";
import {
  MAINTENANCE_ITEMS_MEDIA_TOTAL_LIMIT,
  shouldHideMaintenanceRefillReplaceFields,
  arePeriodicMaintenanceFieldsValid,
  type MaintenanceItemEntry,
  isScheduledMaintenanceItem,
} from "./maintenanceItemShared";
import { MaintenanceResultType } from "@app/constants";
import { Report } from "@app/types";

export type CreateMaintenanceFormDraft = {
  workAt: Date | null;
  serviceMeterValue: string;
  odometerValue: string;
  periodicSwitchOn: boolean;
  maintenanceIntervalIds: string[];
  workScopeId: string;
  maintenanceItems: MaintenanceItemEntry[];
};

type ApiTopLevelMedia = {
  fileName: string;
  filePath: string;
  mimeType: string;
};

type ApiTopLevelRecording = {
  fileName: string;
  filePath: string;
  mimeType: string;
};

type ApiItemMedia = ApiTopLevelMedia & {
  captureMediaAt: string;
};

type ApiItemRecording = ApiTopLevelRecording & {
  recordingAt: string;
};

type ApiItem = {
  periodicMaintenanceMachineItemId?: string;
  regularMaintenanceItemChoiceId?: string | null;
  maintenanceCategoryId: string | null;
  maintenanceTypeId: string | null;
  maintenanceTypeOther: string | null;
  maintenanceItemId: string | null;
  maintenanceItemOther: string | null;
  refillAmount: number | null;
  isRefill: boolean;
  isReplace: boolean;
  comment: string;
  result: MaintenanceResultType | null;
  action: string | null;
  itemMedias: ApiItemMedia[];
  itemRecordings: ApiItemRecording[];
};
type CreateMaintenanceReportPayload = {
  workAt: string;
  serviceMeterInHour: number | null;
  odometer: number | null;
  signatureUrl: string | null;
  isPeriodicMaintenance: boolean;
  maintenanceWorkScopeId: string | null;
  regularMaintenanceItemChoiceIds: string[];
  items: ApiItem[];
};

type CreateMaintenanceReportDraftPayload = CreateMaintenanceReportPayload & {
  currentStatus: "DRAFT";
};

type UpdateMaintenanceReportPayload = CreateMaintenanceReportPayload & {
  machineReportId: string;
  maintenanceReportId?: string;
  currentStatus: "POSTED" | "DRAFT";
  lastUpdatedSignatureUrl?: string;
};

function parseOptionalNumeric(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = parseFloat(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function nullableTrim(value: string | null | undefined): string | null {
  const trimmed = (value ?? "").trim();
  return trimmed.length > 0 ? trimmed : null;
}

async function buildCommonMediaFields(
  entry: MaintenanceItemEntry,
): Promise<{ itemMedias: ApiItemMedia[]; itemRecordings: ApiItemRecording[] }> {
  const uploadedMedias = await uploadMediaFiles(entry.mediaFiles);
  const uploadedRecordings = await uploadRecording(entry.recordedBlob);

  const newItemMedias: ApiItemMedia[] = uploadedMedias.map((media) => ({
    fileName: media.fileName,
    filePath: media.filePath,
    mimeType: media.mimeType,
    captureMediaAt: media.captureMediaAt,
  }));

  const newItemRecordings: ApiItemRecording[] = uploadedRecordings.map(
    (recording) => ({
      fileName: recording.fileName,
      filePath: recording.filePath,
      mimeType: recording.mimeType,
      recordingAt: recording.recordingAt,
    }),
  );

  // Existing media that wasn't touched this session still needs to be
  // re-declared: PUT deletes and recreates every item row, so anything
  // not included here is gone even if the user never removed it.
  const existingItemMedias: ApiItemMedia[] = (
    entry.existingItemMedias ?? []
  ).map((media) => ({
    fileName: media.fileName ?? "",
    filePath: media.filePath ?? media.mediaUrl ?? "",
    mimeType: media.mimeType ?? "application/octet-stream",
    captureMediaAt:
      media.captureMediaAt ?? media.createdAt ?? new Date().toISOString(),
  }));

  const existingItemRecording: ApiItemRecording[] = entry.existingItemRecording
    ? [
        {
          fileName: entry.existingItemRecording.fileName ?? "",
          filePath:
            entry.existingItemRecording.filePath ??
            entry.existingItemRecording.recordingUrl ??
            "",
          mimeType: entry.existingItemRecording.mimeType ?? "audio/webm",
          recordingAt:
            entry.existingItemRecording.recordingAt ??
            entry.existingItemRecording.createdAt ??
            new Date().toISOString(),
        },
      ]
    : [];

  return {
    itemMedias: [...existingItemMedias, ...newItemMedias],
    // a freshly recorded blob replaces any existing recording outright
    // (one recording max per item, per your confirmation)
    itemRecordings: newItemRecordings.length
      ? newItemRecordings
      : existingItemRecording,
  };
}

async function buildItemPayload(entry: MaintenanceItemEntry): Promise<ApiItem> {
  const { itemMedias, itemRecordings } = await buildCommonMediaFields(entry);

  if (isScheduledMaintenanceItem(entry)) {
    return {
      periodicMaintenanceMachineItemId: entry.periodicMaintenanceMachineItemId,
      regularMaintenanceItemChoiceId: entry.regularMaintenanceItemChoiceId,
      maintenanceCategoryId: null,
      maintenanceTypeId: null,
      maintenanceTypeOther: null,
      maintenanceItemId: null,
      maintenanceItemOther: null,
      refillAmount: null,
      isRefill: false,
      isReplace: false,
      comment: entry.comment.trim(),
      result: entry.resultValue ?? null,
      action: null,
      itemMedias,
      itemRecordings,
    };
  }

  const hideRefillReplace = shouldHideMaintenanceRefillReplaceFields(
    entry.categoryCode,
  );

  const refillAmount = hideRefillReplace
    ? null
    : parseOptionalNumeric(entry.refillAmount);

  return {
    maintenanceCategoryId: entry.categoryId,
    maintenanceTypeId: entry.maintenanceTypeId ? entry.maintenanceTypeId : null,
    maintenanceTypeOther: null,
    maintenanceItemId: entry.maintenanceItemId,
    maintenanceItemOther: nullableTrim(entry.maintenanceItemOther),
    result: entry.resultValue ?? null,
    action: entry.action ? entry.action.toUpperCase() : null,
    refillAmount,
    isRefill: !hideRefillReplace && entry.action === "refill",
    isReplace: !hideRefillReplace && entry.action === "replace",
    comment: entry.comment.trim(),
    itemMedias,
    itemRecordings,
  };
}

export async function submitCreateMaintenance(params: {
  groupId: string;
  machineId: string;
  draft: CreateMaintenanceFormDraft;
  signatureDataUrl: string | null;
}): Promise<{ successMessage?: string }> {
  const { groupId, machineId, draft, signatureDataUrl } = params;

  if (!draft.workAt || !isWorkAtInValidRange(draft.workAt)) {
    throw new Error("WORK_AT_INVALID");
  }

  const serviceMeterInHour = parseOptionalNumeric(draft.serviceMeterValue);
  if (serviceMeterInHour === null) {
    throw new Error("SERVICE_METER_REQUIRED");
  }

  const odometer = parseOptionalNumeric(draft.odometerValue);

  const totalItemsMediaCount = draft.maintenanceItems.reduce(
    (sum, item) => sum + item.mediaFiles.length,
    0,
  );
  if (totalItemsMediaCount > MAINTENANCE_ITEMS_MEDIA_TOTAL_LIMIT) {
    throw new Error("MAINTENANCE_ITEMS_MEDIA_TOTAL_EXCEEDED");
  }

  if (
    !arePeriodicMaintenanceFieldsValid(
      draft.periodicSwitchOn,
      draft.maintenanceIntervalIds,
    )
  ) {
    throw new Error("PERIODIC_MAINTENANCE_FIELDS_REQUIRED");
  }

  const signatureUrl = signatureDataUrl
    ? await uploadSignature(signatureDataUrl)
    : null;

  const items: ApiItem[] = await Promise.all(
    draft.maintenanceItems.map(buildItemPayload),
  );

  const scheduleIntervalIds = draft.maintenanceItems
    .filter(isScheduledMaintenanceItem)
    .map((item) => item.regularMaintenanceItemChoiceId);

  const regularMaintenanceItemChoiceIds = Array.from(
    new Set([...draft.maintenanceIntervalIds, ...scheduleIntervalIds]),
  );

  const payload: CreateMaintenanceReportPayload = {
    workAt: draft.workAt.toISOString(),
    serviceMeterInHour,
    odometer,
    signatureUrl,
    isPeriodicMaintenance: draft.periodicSwitchOn,
    maintenanceWorkScopeId: draft.workScopeId ? draft.workScopeId : null,
    regularMaintenanceItemChoiceIds,
    items,
  };

  const response = await apiPostMaintenanceReport(groupId, machineId, payload);

  return {
    successMessage: response?.data?.meta?.successMessage,
  };
}

export async function submitUpdateMaintenance(params: {
  groupId: string;
  machineId: string;
  report: Pick<Report, "machineReportId"> &
    Partial<Pick<Report, "maintenanceReportId">>;
  draft: CreateMaintenanceFormDraft;
  signatureDataUrl: string | null;
  saveAsDraft?: boolean;
}): Promise<{ successMessage?: string; machineReportId?: string }> {
  const { groupId, machineId, report, draft, signatureDataUrl, saveAsDraft } =
    params;

  if (!draft.workAt || !isWorkAtInValidRange(draft.workAt)) {
    throw new Error("WORK_AT_INVALID");
  }

  const serviceMeterInHour = parseOptionalNumeric(draft.serviceMeterValue);
  if (!saveAsDraft && serviceMeterInHour === null) {
    throw new Error("SERVICE_METER_REQUIRED");
  }

  const odometer = parseOptionalNumeric(draft.odometerValue);

  const totalItemsMediaCount = draft.maintenanceItems.reduce(
    (sum, item) =>
      sum + item.mediaFiles.length + (item.existingItemMedias?.length ?? 0),
    0,
  );
  if (totalItemsMediaCount > MAINTENANCE_ITEMS_MEDIA_TOTAL_LIMIT) {
    throw new Error("MAINTENANCE_ITEMS_MEDIA_TOTAL_EXCEEDED");
  }

  if (
    !saveAsDraft &&
    !arePeriodicMaintenanceFieldsValid(
      draft.periodicSwitchOn,
      draft.maintenanceIntervalIds,
    )
  ) {
    throw new Error("PERIODIC_MAINTENANCE_FIELDS_REQUIRED");
  }

  const lastUpdatedSignatureUrl = signatureDataUrl
    ? await uploadSignature(signatureDataUrl)
    : undefined;

  const items: ApiItem[] = await Promise.all(
    draft.maintenanceItems.map(buildItemPayload),
  );

  const scheduleIntervalIds = draft.maintenanceItems
    .filter(isScheduledMaintenanceItem)
    .map((item) => item.regularMaintenanceItemChoiceId);

  const regularMaintenanceItemChoiceIds = Array.from(
    new Set([...draft.maintenanceIntervalIds, ...scheduleIntervalIds]),
  );

  const payload: UpdateMaintenanceReportPayload = {
    machineReportId: report.machineReportId,
    ...(report.maintenanceReportId
      ? { maintenanceReportId: report.maintenanceReportId }
      : {}),
    workAt: draft.workAt.toISOString(),
    currentStatus: saveAsDraft ? "DRAFT" : "POSTED",
    serviceMeterInHour,
    odometer,
    signatureUrl: null,
    ...(lastUpdatedSignatureUrl ? { lastUpdatedSignatureUrl } : {}),
    isPeriodicMaintenance: draft.periodicSwitchOn,
    maintenanceWorkScopeId: draft.workScopeId ? draft.workScopeId : null,
    regularMaintenanceItemChoiceIds,
    items,
  };

  const response = await apiPutMaintenanceReport(
    groupId,
    machineId,
    report.machineReportId,
    payload,
  );

  return {
    successMessage: response?.data?.meta?.successMessage,
    machineReportId: report.machineReportId,
  };
}

export async function submitCreateMaintenanceDraft(params: {
  groupId: string;
  machineId: string;
  draft: CreateMaintenanceFormDraft;
}): Promise<{ successMessage?: string; machineReportId?: string }> {
  const { groupId, machineId, draft } = params;

  if (!draft.workAt || !isWorkAtInValidRange(draft.workAt)) {
    throw new Error("WORK_AT_INVALID");
  }

  const serviceMeterInHour = parseOptionalNumeric(draft.serviceMeterValue);
  const odometer = parseOptionalNumeric(draft.odometerValue);

  const totalItemsMediaCount = draft.maintenanceItems.reduce(
    (sum, item) => sum + item.mediaFiles.length,
    0,
  );
  if (totalItemsMediaCount > MAINTENANCE_ITEMS_MEDIA_TOTAL_LIMIT) {
    throw new Error("MAINTENANCE_ITEMS_MEDIA_TOTAL_EXCEEDED");
  }

  const items: ApiItem[] = await Promise.all(
    draft.maintenanceItems.map(buildItemPayload),
  );

  const scheduleIntervalIds = draft.maintenanceItems
    .filter(isScheduledMaintenanceItem)
    .map((item) => item.regularMaintenanceItemChoiceId);

  const regularMaintenanceItemChoiceIds = Array.from(
    new Set([...draft.maintenanceIntervalIds, ...scheduleIntervalIds]),
  );

  const payload: CreateMaintenanceReportDraftPayload = {
    workAt: draft.workAt.toISOString(),
    currentStatus: "DRAFT",
    serviceMeterInHour,
    odometer,
    signatureUrl: null,
    isPeriodicMaintenance: draft.periodicSwitchOn,
    maintenanceWorkScopeId: draft.workScopeId ? draft.workScopeId : null,
    regularMaintenanceItemChoiceIds,
    items,
  };

  const response = await apiPostMaintenanceReportDraft(
    groupId,
    machineId,
    payload,
  );

  return {
    successMessage: response?.data?.meta?.successMessage,
    machineReportId: response?.data?.data?.machineReportId,
  };
}
